import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { IntercomService } from '@core/providers/backend/intercom.service';
import { TooltipWidgetDirective } from '@shared/components/tooltip-widget/tooltip-widget.directive';
import {
  AI_DISCLOSURE_ARTICLE_ID,
  AI_DISCLOSURE_KEYS,
  AI_DISCLOSURE_TOOLTIP_DELAY_MS,
  AiDisclosureKind,
  AiDisclosureVariant,
} from './ai-disclosure.constants';

/**
 * EU AI Act Article 50(1) disclosure. Renders text, never a hover affordance — the obligation is
 * to inform at first exposure, so anything that only appears on `mouseenter` cannot carry it.
 *
 * Callers pick a `kind`, not wording: keeping the strings unreachable from the outside is what
 * makes one source of truth an API property rather than a convention.
 */
@Component({
  selector: 'app-ai-disclosure',
  template: `
    <span
      class="ai-disclosure"
      [class.ai-disclosure--note]="variant() === 'note'"
      [class.ai-disclosure--icon]="variant() === 'icon'"
      appTooltipWidget
      [showToolTip]="variant() !== 'note'"
      [showDelay]="tooltipDelayMs"
      [contentTemplate]="aboutFoodbackIntelligence"
      panelClass="Overview-tooltip">
      @if (showIcon()) {
        <span class="ai-disclosure__icon" aria-hidden="true"></span>
      }
      @if (textKey()) {
        <span class="ai-disclosure__text">{{ textKey() | translate }}</span>
      } @else {
        <span class="ai-disclosure__sr-only">{{ keys.label[kind()] | translate }}</span>
      }
      @if (showLearnMore()) {
        <button
          type="button"
          class="ai-disclosure__link"
          [attr.data-testid]="testId() + '-learn-more'"
          (click)="openHelpArticle($event)">
          {{ keys.learnMore | translate }}
        </button>
      }
    </span>

    <ng-template #aboutFoodbackIntelligence>
      @if (kind() === 'translation') {
        <span>{{ keys.body.translation | translate }}</span>
      } @else {
        <strong>{{ keys.label[kind()] | translate }}</strong>
        <br />
        <span
          >{{ keys.aboutIntro | translate }}<br /><br />{{ keys.aboutBody | translate }}
          @if (kind() === 'statistical') {
            <br /><br />{{ keys.body.statistical | translate }}
          }
        </span>
      }
      <br />
      <a href="#" data-testid="ai-disclosure-about-learn-more" (click)="openHelpArticle($event)">{{
        keys.learnMore | translate
      }}</a>
    </ng-template>
  `,
  styleUrl: './ai-disclosure.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, TooltipWidgetDirective],
  host: { '[attr.data-testid]': 'testId()' },
})
export class AiDisclosureComponent {
  readonly kind = input<AiDisclosureKind>('generative');
  readonly variant = input<AiDisclosureVariant>('note');
  readonly showLearnMore = input(true);
  /** Off where the mark is repeated per row and the icon would be visual noise. */
  readonly showIcon = input(true);
  readonly testId = input('ai-disclosure');
  /** Lets a host rendered inside an overlay dismiss it before the help article takes over. */
  readonly helpArticleOpened = output<void>();

  private readonly intercomService = inject(IntercomService);

  protected readonly keys = AI_DISCLOSURE_KEYS;
  protected readonly tooltipDelayMs = AI_DISCLOSURE_TOOLTIP_DELAY_MS;

  protected readonly textKey = computed<string | null>(() => {
    switch (this.variant()) {
      case 'chip':
        return AI_DISCLOSURE_KEYS.label[this.kind()];
      case 'icon':
        return null;
      default:
        return AI_DISCLOSURE_KEYS.body[this.kind()];
    }
  });

  protected openHelpArticle(event: MouseEvent): void {
    this.intercomService.openArticleLink(event, AI_DISCLOSURE_ARTICLE_ID);
    this.helpArticleOpened.emit();
  }
}
